// Countdown Images Service for app-level storage
import { isSupabaseConfigured } from '../config/supabase';
import { queuedSupabaseOperation } from '../utils/supabaseWrapper';

/**
 * Load all countdown images from Supabase
 */
export const loadCountdownImages = async () => {
  if (!isSupabaseConfigured()) {
    console.warn('Supabase not configured, loading from localStorage');
    try {
      const saved = localStorage.getItem('countdown_images');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading from localStorage:', error);
      return [];
    }
  }

  try {
    console.log('📂 Loading countdown images from Supabase...');
    
    const data = await queuedSupabaseOperation.select(
      'countdown_images',
      '*',
      'order=created_at.desc',
      'countdown_images', // localStorage fallback key
      'Load countdown images'
    );

    // Transform Supabase data to match the old localStorage format
    const images = data ? data.map(row => ({
      id: row.id,
      data: row.image_data,
      name: row.name,
      size: row.file_size,
      uploadedAt: row.created_at
    })) : [];

    console.log('✅ Countdown images loaded from Supabase:', images.length, 'images');
    
    return images;
  } catch (error) {
    console.error('❌ Error loading countdown images from Supabase:', error);
    // Fallback to localStorage
    try {
      const saved = localStorage.getItem('countdown_images');
      const fallbackImages = saved ? JSON.parse(saved) : [];
      console.log('🔄 Fallback to localStorage:', fallbackImages.length, 'images');
      return fallbackImages;
    } catch (localError) {
      console.error('❌ Error loading from localStorage fallback:', localError);
      return [];
    }
  }
};

/**
 * Save a new countdown image to Supabase
 */
export const saveCountdownImage = async (imageData) => {
  if (!isSupabaseConfigured()) {
    console.warn('Supabase not configured, saving to localStorage');
    try {
      const existing = JSON.parse(localStorage.getItem('countdown_images') || '[]');
      const updated = [...existing, imageData];
      localStorage.setItem('countdown_images', JSON.stringify(updated));
      return { data: imageData, error: null };
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      return { data: null, error };
    }
  }

  try {
    console.log('💾 Saving countdown image to Supabase using queue...');
    console.log('🔍 Image data size:', imageData.data?.length || 'unknown', 'characters');
    console.log('🔍 Image name:', imageData.name);
    console.log('🔍 Image file size:', imageData.size, 'bytes');
    
    const insertData = {
      name: imageData.name,
      image_data: imageData.data,
      file_size: imageData.size,
      mime_type: imageData.mimeType || 'image/jpeg',
      created_at: new Date().toISOString()
    };
    
    const result = await queuedSupabaseOperation.insert(
      'countdown_images',
      insertData,
      'countdown_images', // localStorage fallback key
      'Save countdown image'
    );

    console.log('✅ Countdown image saved to Supabase via queue');
    
    // Transform back to the expected format
    const savedImage = {
      id: result.id,
      data: result.image_data,
      name: result.name,
      size: result.file_size,
      uploadedAt: result.created_at
    };

    return { data: savedImage, error: null };
  } catch (error) {
    console.error('❌ Error saving countdown image via queue:', error);
    return { data: null, error };
  }
};

/**
 * Delete a countdown image from Supabase
 */
export const deleteCountdownImage = async (imageId) => {
  if (!isSupabaseConfigured()) {
    console.warn('Supabase not configured, deleting from localStorage');
    try {
      const existing = JSON.parse(localStorage.getItem('countdown_images') || '[]');
      const updated = existing.filter(img => img.id !== imageId);
      localStorage.setItem('countdown_images', JSON.stringify(updated));
      return { error: null };
    } catch (error) {
      console.error('Error deleting from localStorage:', error);
      return { error };
    }
  }

  try {
    console.log('🗑️ Deleting countdown image from Supabase via queue...', imageId);
    
    await queuedSupabaseOperation.delete(
      'countdown_images',
      `id=eq.${imageId}`,
      'countdown_images', // localStorage fallback key
      'Delete countdown image'
    );

    console.log('✅ Countdown image deleted from Supabase via queue');

    return { error: null };
  } catch (error) {
    console.error('❌ Error deleting countdown image:', error);
    
    // Fallback to localStorage
    try {
      const existing = JSON.parse(localStorage.getItem('countdown_images') || '[]');
      const updated = existing.filter(img => img.id !== imageId);
      localStorage.setItem('countdown_images', JSON.stringify(updated));
      return { error: null };
    } catch (localError) {
      console.error('❌ Error deleting from localStorage fallback:', localError);
      return { error };
    }
  }
};

/**
 * Clear all countdown images from Supabase
 */
export const clearAllCountdownImages = async () => {
  if (!isSupabaseConfigured()) {
    console.warn('Supabase not configured, clearing localStorage');
    localStorage.removeItem('countdown_images');
    return { error: null };
  }

  try {
    console.log('🧹 Clearing all countdown images from Supabase via queue...');
    
    await queuedSupabaseOperation.delete(
      'countdown_images',
      'id=neq.00000000-0000-0000-0000-000000000000', // Delete all rows
      'countdown_images', // localStorage fallback key  
      'Clear all countdown images'
    );

    console.log('✅ All countdown images cleared from Supabase via queue');
    
    // Also clear localStorage
    localStorage.removeItem('countdown_images');

    return { error: null };
  } catch (error) {
    console.error('❌ Error clearing countdown images:', error);
    
    // Fallback to localStorage
    localStorage.removeItem('countdown_images');
    return { error: null };
  }
};

